import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { 
  HomeIcon, 
  ArrowLeftIcon, 
  BookOpenIcon, 
  HeartIcon, 
  EnvelopeIcon 
} from '@heroicons/react/24/outline'

export default function NotFound() {
  const quickLinks = [
    { name: 'Home', href: '/', icon: HomeIcon, desc: 'Return to the homepage' },
    { name: 'Our Programs', href: '/programs', icon: BookOpenIcon, desc: 'Explore education & health projects' },
    { name: 'Goodwill Medical Centre', href: '/goodwill-medical', icon: HeartIcon, desc: 'Learn about our community hospital' },
    { name: 'Contact Us', href: '/contact', icon: EnvelopeIcon, desc: 'Get in touch with our team' },
  ]

  return (
    <>
      <Helmet>
        <title>Page Not Found | PACIEH</title>
        <meta name="description" content="The page you are looking for does not exist on the PACIEH website." />
      </Helmet>

      <div className="min-h-[80vh] flex items-center justify-center pt-24 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-2xl w-full text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-pacieh-green/10 text-pacieh-green mb-6">
              <span className="text-3xl font-bold font-display">404</span>
            </div>

            <h1 className="text-3xl font-extrabold text-pacieh-blue tracking-tight sm:text-5xl font-display mb-4">
              Page Not Found
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
              Sorry, we couldn’t find the page you’re looking for. It might have been moved or doesn't exist.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-pacieh-green text-white font-semibold hover:bg-pacieh-blue shadow-md transition-all duration-200"
              >
                <ArrowLeftIcon className="h-5 w-5" />
                Back to Homepage
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-all duration-200"
              >
                Contact Support
              </Link>
            </div>

            <div className="border-t border-gray-200 pt-8 text-left">
              <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 text-center">
                Popular Destinations
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {quickLinks.map((item) => {
                  const Icon = item.icon
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="p-4 rounded-xl border border-gray-100 bg-white hover:border-pacieh-green/40 hover:shadow-md transition-all group flex items-start gap-3"
                    >
                      <div className="p-2 rounded-lg bg-pacieh-blue/5 text-pacieh-blue group-hover:bg-pacieh-green group-hover:text-white transition-colors">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 group-hover:text-pacieh-green transition-colors text-sm">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.desc}
                        </div>
                      </div>
                    </Link>
                  )
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  )
}